import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef, useMemo } from 'react';

// Types for language structure
export type Language = 'en' | 'id';

export type TranslationStructure = {
  navigation: {
    dashboard: string;
    visits: string;
    customers: string;
    interactions: string;
    products: string;
    reports: string;
    profile: string;
  };
  sidebar: {
    profile: string;
    settings: string;
    preferences: string;
    notifications: string;
    language: string;
    help: string;
    about: string;
    logout: string;
  };
  dashboard: {
    title: string;
    welcomeBack: string;
    quickStats: string;
    totalVisits: string;
    completedVisits: string;
    activeCustomers: string;
    todayVisits: string;
    recentActivity: string;
    viewAll: string;
    noActivity: string;
  };
  visits: {
    title: string;
    activeVisit: string;
    startVisit: string;
    endVisit: string;
    visitHistory: string;
    noActiveVisit: string;
    visitInProgress: string;
    selectCustomer: string;
    visitNotes: string;
    evidence: string;
    location: string;
  };
  customers: {
    title: string;
    addNew: string;
    searchCustomers: string;
    customerDetails: string;
    contactInfo: string;
    address: string;
    phone: string;
    email: string;
    status: string;
    active: string;
    inactive: string;
    lastVisit: string;
    totalVisits: string;
  };
  interactions: {
    title: string;
    newInteraction: string;
    createCurrent: string;
    scheduleInteraction: string;
    recordCompleted: string;
    planFuture: string;
    customer: string;
    subject: string;
    notes: string;
    priority: string;
    date: string;
    time: string;
    status: string;
    completed: string;
    pending: string;
    cancelled: string;
    interaction_modal: InteractionModalTranslations;
  };
  reports: {
    title: string;
    visitsTimeline: string;
    performanceMetrics: string;
    completionRate: string;
    totalVisits: string;
    averageDuration: string;
    customerSatisfaction: string;
    monthlyReport: string;
    weeklyReport: string;
    dailyReport: string;
    exportReport: string;
  };
  modals: {
    newInteraction: {
      title: string;
      scheduleTitle: string;
      selectCustomer: string;
      searchPlaceholder: string;
      customer: string;
      subject: string;
      notes: string;
      priority: string;
      date: string;
      time: string;
      cancel: string;
      create: string;
      schedule: string;
      creating: string;
      scheduling: string;
      addInteraction: string;
      searchCustomers: string;
      noCustomersFound: string;
      typeAtLeast2: string;
      loadMore: string;
      addVoiceNote: string;
      hideVoiceInput: string;
      lastVoiceNote: string;
    };
    language: {
      title: string;
      subtitle: string;
      english: string;
      indonesian: string;
      cancel: string;
      apply: string;
    };
    customerDetail: {
      title: string;
      contactInfo: string;
      visitHistory: string;
      recentInteractions: string;
      close: string;
    };
    evidenceCapture: {
      title: string;
      takePhoto: string;
      recordVideo: string;
      recordAudio: string;
      selectFile: string;
      save: string;
      cancel: string;
    };
    logout: {
      title: string;
      message: string;
      confirm: string;
      cancel: string;
    };
  };
  forms: {
    validation: {
      required: string;
      invalidEmail: string;
      passwordTooShort: string;
      passwordsNotMatch: string;
      invalidPhone: string;
      pleaseSelectCustomer: string;
      pleaseSelectDate: string;
      failedToCreate: string;
      unexpectedError: string;
    };
    placeholders: {
      searchCustomer: string;
      enterSubject: string;
      addNotes: string;
      selectDate: string;
      selectTime: string;
    };
  };
  common: {
    buttons: {
      save: string;
      cancel: string;
      delete: string;
      edit: string;
      close: string;
      ok: string;
      yes: string;
      no: string;
      back: string;
      next: string;
      confirm: string;
      submit: string;
      reset: string;
      apply: string;
      create: string;
      update: string;
    };
    status: {
      loading: string;
      saving: string;
      success: string;
      error: string;
      completed: string;
      pending: string;
      active: string;
      inactive: string;
      cancelled: string;
    };
    time: {
      today: string;
      yesterday: string;
      tomorrow: string;
      thisWeek: string;
      lastWeek: string;
      thisMonth: string;
      lastMonth: string;
      ago: string;
      now: string;
    };
    messages: {
      noData: string;
      noResults: string;
      loadingData: string;
      saveSuccessful: string;
      saveError: string;
      deleteConfirm: string;
      operationSuccessful: string;
      operationFailed: string;
    };
  };
  priorities: {
    low: string;
    medium: string;
    high: string;
  };
  customerTypes: {
    aquaculture: string;
    retail: string;
    wholesale: string;
    distributor: string;
  };
  all_customers: {
    title: string;
    clear_search: string;
    call_customer: string;
    whatsapp_message: string;
    call: string;
    whatsapp: string;
    last_interaction: string;
    no_recent_interaction: string;
    no_customers_available: string;
    view_mode: string;
    list_view: string;
    list_view_desc: string;
    map_view: string;
    map_view_desc: string;
    map_statistics: string;
    customer_options: string;
  };
};

// Enhanced translation validation types
type TranslationValidationResult = {
  isValid: boolean;
  missingKeys: string[];
  invalidKeys: string[];
  totalKeys: number;
  coverage: number;
};

type TranslationError = {
  key: string;
  error: string;
  timestamp: number;
};

type InteractionModalTranslations = {
  [key: string]: string;
  customer_required: string;
  date_required: string;
  unexpected_error: string;
  customer_label: string;
  search_placeholder: string;
  cancel_button: string;
  creating_button: string;
  scheduling_button: string;
};

type LanguageSwitchResult = {
  success: boolean;
  previousLanguage: Language;
  newLanguage: Language;
  validationResult?: TranslationValidationResult;
  error?: string;
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  translations: TranslationStructure;
  t: (key: string, fallbackValue?: string) => string;
  isLoading: boolean;
  isInitialized: boolean;
  // Enhanced validation methods
  validateTranslationKey: (key: string) => boolean;
  getAllMissingKeys: (keyList: string[]) => string[];
  getTranslationStatus: () => { loading: boolean; error: string | null; language: Language };
  switchLanguageWithValidation: (lang: Language) => Promise<LanguageSwitchResult>;
  exportTranslationReport: () => any;
  testAllTranslations: (keyList?: string[]) => TranslationValidationResult;
  simulateTranslationFailure: (shouldFail: boolean) => void;
  // Error recovery and caching
  retryTranslationLoad: (lang: Language) => Promise<boolean>;
  clearTranslationCache: () => void;
  preloadLanguage: (lang: Language) => Promise<boolean>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

// Function to detect user's preferred language from browser settings
const detectUserLanguage = (): Language => {
  // Check if we have a saved language preference
  const saved = localStorage.getItem('app-language');
  if (saved === 'en' || saved === 'id') {
    return saved;
  }

  // Detect from user-agent/browser language settings
  try {
    const browserLanguages = navigator.languages || [navigator.language || 'en'];
    
    for (const lang of browserLanguages) {
      // Check for Indonesian language variations
      if (lang.toLowerCase().startsWith('id') || lang.toLowerCase().includes('indonesia')) {
        return 'id';
      }
      // Check for English language variations
      if (lang.toLowerCase().startsWith('en')) {
        return 'en';
      }
    }
  } catch (error) {
    console.warn('Failed to detect browser language:', error);
  }

  // Default fallback to English if no match found
  return 'en';
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    return detectUserLanguage();
  });

  const [translations, setTranslations] = useState<TranslationStructure>({} as TranslationStructure);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Enhanced state for validation and testing
  const [translationErrors, setTranslationErrors] = useState<TranslationError[]>([]);
  const [translationCache, setTranslationCache] = useState<Map<Language, TranslationStructure>>(new Map());
  const [loadAttempts, setLoadAttempts] = useState<Map<Language, number>>(new Map());
  const [simulateFailure, setSimulateFailure] = useState(false);
  const [lastValidationResult, setLastValidationResult] = useState<TranslationValidationResult | null>(null);

  // Ref to queue missing keys without causing state mutations during render
  const missingKeyQueueRef = useRef<TranslationError[]>([]);
  const fallbackWarningLoggedRef = useRef(false);

  // Optimized function to load translations with caching and error recovery
  const loadTranslations = async (lang: Language) => {
    setIsLoading(true);
    setIsInitialized(false);

    // Simulate failure for testing
    if (simulateFailure) {
      setIsLoading(false);
      throw new Error(`Simulated failure for ${lang}`);
    }

    try {
      // Check cache first - FAST PATH
      if (translationCache.has(lang)) {
        setTranslations(translationCache.get(lang)!);
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }

      // Fetch translation file
      const response = await fetch(`/lang_${lang}.json`);

      if (!response.ok) {
        throw new Error(`Failed to load ${lang} translations: ${response.status}`);
      }

      const data = await response.json();

      // Extract the translations from the nested structure
      const langData = data[lang];
      if (langData) {
        // Quick validation of critical sections (simplified)
        const criticalSections = ['interactions', 'common', 'forms', 'navigation'];
        const missingSections = criticalSections.filter(section => !langData[section]);
        if (missingSections.length > 0) {
          setTranslationErrors(prev => [...prev.slice(-20), {
            key: `missing_sections_${lang}`,
            error: `Missing sections: ${missingSections.join(', ')}`,
            timestamp: Date.now()
          }]);
        }

        // Simplified interaction_modal validation and auto-fix
        let interactionModal = null;

        // Check and fix structure efficiently
        if (langData.interactions?.interaction_modal) {
          interactionModal = langData.interactions.interaction_modal;
          // Fix double nesting if exists
          if (interactionModal.interaction_modal && typeof interactionModal.interaction_modal === 'object') {
            langData.interactions.interaction_modal = interactionModal.interaction_modal;
            interactionModal = interactionModal.interaction_modal;
          }
        } else if (langData.interaction_modal) {
          // Move from root to nested
          interactionModal = langData.interaction_modal;
          if (interactionModal.interaction_modal && typeof interactionModal.interaction_modal === 'object') {
            interactionModal = interactionModal.interaction_modal;
          }
          langData.interactions = langData.interactions || {};
          langData.interactions.interaction_modal = interactionModal;
        } else {
          // Check alternative locations
          const possibleLocations = [
            langData.modals?.interaction_modal,
            langData.forms?.interaction_modal,
            langData.modal?.interaction_modal
          ];

          for (const location of possibleLocations) {
            if (location && typeof location === 'object') {
              interactionModal = location;
              break;
            }
          }

          if (interactionModal) {
            langData.interactions = langData.interactions || {};
            langData.interactions.interaction_modal = interactionModal;
          } else {
            // Create minimal fallback
            const minimalModal = {
              customer_required: 'Please select a customer',
              date_required: 'Please select a valid date',
              unexpected_error: 'An unexpected error occurred',
              customer_label: 'Customer *',
              search_placeholder: 'Search and select customer...',
              cancel_button: 'Cancel',
              creating_button: 'Creating...',
              scheduling_button: 'Scheduling...'
            };
            langData.interactions = langData.interactions || {};
            langData.interactions.interaction_modal = minimalModal;
            interactionModal = minimalModal;
          }
        }

        // Quick validation and auto-fix missing keys
        if (interactionModal) {
          const requiredKeys = ['customer_required', 'date_required', 'customer_label', 'search_placeholder', 'cancel_button', 'creating_button', 'scheduling_button'];
          const fallbacks: Record<string, string> = {
            customer_required: 'Please select a customer',
            date_required: 'Please select a valid date',
            customer_label: 'Customer *',
            search_placeholder: 'Search and select customer...',
            cancel_button: 'Cancel',
            creating_button: 'Creating...',
            scheduling_button: 'Scheduling...'
          };

          // Auto-fix missing keys
          requiredKeys.forEach(key => {
            if (!interactionModal[key] || typeof interactionModal[key] !== 'string' || !interactionModal[key].trim()) {
              interactionModal[key] = fallbacks[key];
            }
          });
        }

        // Cache the translations (limit cache size)
        setTranslationCache(prev => {
          const newCache = new Map(prev);
          if (newCache.size > 5) {
            const firstKey = newCache.keys().next().value;
            newCache.delete(firstKey);
          }
          return newCache.set(lang, langData);
        });
        setTranslations(langData);
        setIsInitialized(true);
      } else {
        throw new Error(`No translations found for language: ${lang}`);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';

      // Keep only last 20 errors
      setTranslationErrors(prev => [...prev.slice(-19), {
        key: `load_error_${lang}`,
        error: errorMsg,
        timestamp: Date.now()
      }]);

      // Simplified fallback logic
      if (lang !== 'en') {
        const attempts = loadAttempts.get(lang) || 0;

        if (attempts < 1) {
          setLoadAttempts(prev => new Map(prev.set(lang, attempts + 1)));
          await new Promise(resolve => setTimeout(resolve, 500));
          return await loadTranslations(lang);
        } else {
          return await loadTranslations('en');
        }
      } else {
        // Emergency fallback translations
        const emergencyTranslations = {
          navigation: {
            dashboard: 'Dashboard',
            visits: 'Visits',
            customers: 'Customers',
            interactions: 'Interactions',
            products: 'Products',
            reports: 'Reports',
            profile: 'Profile'
          },
          common: {
            buttons: {
              save: 'Save',
              cancel: 'Cancel',
              delete: 'Delete',
              edit: 'Edit',
              close: 'Close',
              ok: 'OK',
              yes: 'Yes',
              no: 'No',
              back: 'Back',
              next: 'Next',
              create: 'Create',
              update: 'Update'
            },
            status: {
              loading: 'Loading...',
              saving: 'Saving...',
              success: 'Success',
              error: 'Error',
              completed: 'Completed',
              pending: 'Pending',
              active: 'Active',
              inactive: 'Inactive'
            },
            messages: {
              noData: 'No data available',
              loadingData: 'Loading data...',
              saveSuccessful: 'Successfully saved',
              saveError: 'Failed to save',
              operationSuccessful: 'Operation successful',
              operationFailed: 'Operation failed'
            }
          },
          forms: {
            validation: {
              required: 'This field is required',
              pleaseSelectCustomer: 'Please select a customer',
              pleaseSelectDate: 'Please select a valid date',
              failedToCreate: 'Failed to create. Please try again.',
              unexpectedError: 'An unexpected error occurred. Please try again.'
            },
            placeholders: {
              searchCustomer: 'Search and select customer...',
              enterSubject: 'Enter subject...',
              addNotes: 'Add notes...',
              selectDate: 'Select date',
              selectTime: 'Select time'
            }
          },
          modals: {
            newInteraction: {
              title: 'New Interaction',
              scheduleTitle: 'Schedule Interaction',
              selectCustomer: 'Select Customer',
              searchPlaceholder: 'Search and select customer...',
              customer: 'Customer',
              subject: 'Subject',
              notes: 'Notes',
              priority: 'Priority',
              date: 'Date',
              time: 'Time',
              cancel: 'Cancel',
              create: 'Create',
              schedule: 'Schedule',
              creating: 'Creating...',
              scheduling: 'Scheduling...'
            }
          },
          interactions: {
            title: 'Interactions',
            newInteraction: 'New Interaction',
            createCurrent: 'Create Current Interaction',
            scheduleInteraction: 'Schedule Interaction',
            customer: 'Customer',
            subject: 'Subject',
            notes: 'Notes',
            priority: 'Priority',
            date: 'Date',
            time: 'Time',
            status: 'Status',
            completed: 'Completed',
            pending: 'Pending',
            cancelled: 'Cancelled',
            interaction_modal: {
              customer_required: 'Please select a customer',
              date_required: 'Please select a valid date',
              unexpected_error: 'An unexpected error occurred. Please try again.',
              schedule_future_desc: 'Schedule a future customer interaction',
              log_completed_desc: 'Log your completed customer interaction',
              customer_label: 'Customer *',
              search_placeholder: 'Search and select customer...',
              searching_customers: 'Searching customers...',
              type_min_chars: 'Type at least 2 characters to search...',
              no_customers_found: 'No customers found for',
              load_more_customers: 'Load more customers',
              scroll_to_load: '💡 Scroll down to auto-load more',
              loading_more: 'Loading more customers...',
              interaction_time_desc: 'Specify when this interaction occurred (defaults to current date and time). Use the native date and time pickers for easy selection.',
              notes_details_label: 'Notes & Details',
              cancel_button: 'Cancel',
              scheduling_button: 'Scheduling...',
              creating_button: 'Creating...',
              validation_success: 'All fields validated successfully',
              validation_error: 'Please check the highlighted fields',
              debug_mode_notice: '[DEBUG] Translation validation active',
              fallback_text_used: '[FALLBACK] Using default text for missing key',
              translation_error_count: 'Translation errors found: {count}',
              key_missing_notice: 'Missing translation key: {key}',
              modal_validation_complete: 'Modal validation completed successfully'
            }
          }
        } as any;

        setTranslations(emergencyTranslations);
        setIsInitialized(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Load translations when language changes
  useEffect(() => {
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
    }

    const timeout = setTimeout(() => {
      // Log warning only in development
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Translation loading slow for: ${language}`);
      }
    }, 8000); // Increased to 8s
    setLoadingTimeout(timeout);

    loadTranslations(language).finally(() => {
      if (timeout) clearTimeout(timeout);
      setLoadingTimeout(null);
    });

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('app-language', lang);
  }, []);

  // Enhanced translation validation methods
  const validateTranslationKey = useCallback((key: string): boolean => {
    try {
      if (!translations || Object.keys(translations).length === 0) {
        return false;
      }

      const keys = key.split('.');
      let value: any = translations;

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          return false;
        }
      }

      return typeof value === 'string' && value.trim() !== '' && value !== key;
    } catch (error) {
      console.error(`🌐 [LanguageContext] Error validating key '${key}':`, error);
      return false;
    }
  }, [translations]);

  const getAllMissingKeys = useCallback((keyList: string[]): string[] => {
    return keyList.filter(key => !validateTranslationKey(key));
  }, [validateTranslationKey]);

  const getTranslationStatus = useCallback(() => {
    return {
      loading: isLoading,
      error: translationErrors.length > 0 ? translationErrors[translationErrors.length - 1].error : null,
      language
    };
  }, [isLoading, translationErrors, language]);

  // Optimized translation functions
  const translateInternal = useCallback((key: string, fallbackValue?: string): string => {
    const providedFallback = fallbackValue ?? key;

    if (!translations || Object.keys(translations).length === 0) {
      return providedFallback;
    }

    const keys = key.split('.');
    let value: any = translations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Queue missing key without console.log
        if (process.env.NODE_ENV === 'development') {
          missingKeyQueueRef.current.push({
            key: `missing_key_${key}`,
            error: `Key '${key}' not found`,
            timestamp: Date.now()
          });
        }
        return providedFallback;
      }
    }

    if (typeof value === 'string' && value.trim() && value !== key) {
      return value;
    }

    // Try nested defaults
    if (value && typeof value === 'object') {
      const possibleDefaults = [value.default, value.fallback, value.en, value.text];
      for (const defaultVal of possibleDefaults) {
        if (typeof defaultVal === 'string' && defaultVal.trim()) {
          return defaultVal;
        }
      }
    }

    return providedFallback;
  }, [translations]);

  // Optimized t function
  const t = useCallback((key: string, fallbackValue?: string): string => {
    if (!isInitialized || isLoading) {
      return fallbackValue ?? key;
    }

    try {
      return translateInternal(key, fallbackValue);
    } catch {
      return fallbackValue ?? key;
    }
  }, [isInitialized, isLoading, translateInternal]);

  // Optimized language switching
  const switchLanguageWithValidation = useCallback(async (lang: Language): Promise<LanguageSwitchResult> => {
    const previousLanguage = language;
    const previousTranslations = translations;

    try {
      // Use cached if available
      if (translationCache.has(lang)) {
        const cachedTranslations = translationCache.get(lang)!;
        setTranslations(cachedTranslations);
        setLanguageState(lang);
        localStorage.setItem('app-language', lang);

        return {
          success: true,
          previousLanguage,
          newLanguage: lang
        };
      }

      // Load new language
      setIsLoading(true);
      await loadTranslations(lang);
      await new Promise(resolve => setTimeout(resolve, 50));

      // Validate the new language
      const commonKeys = [
        'common.buttons.save',
        'common.buttons.cancel',
        'interactions.newInteraction',
        'forms.validation.required'
      ];

      const missingKeys = getAllMissingKeys(commonKeys);
      const validationResult: TranslationValidationResult = {
        isValid: missingKeys.length === 0,
        missingKeys,
        invalidKeys: [],
        totalKeys: commonKeys.length,
        coverage: ((commonKeys.length - missingKeys.length) / commonKeys.length) * 100
      };

      if (validationResult.isValid) {
        return {
          success: true,
          previousLanguage,
          newLanguage: lang,
          validationResult
        };
      } else {
        // Rollback
        setLanguageState(previousLanguage);
        setTranslations(previousTranslations);
        localStorage.setItem('app-language', previousLanguage);

        return {
          success: false,
          previousLanguage,
          newLanguage: lang,
          validationResult,
          error: `Validation failed: ${missingKeys.length} missing keys`
        };
      }
    } catch (error) {
      // Rollback
      setLanguageState(previousLanguage);
      setTranslations(previousTranslations);
      localStorage.setItem('app-language', previousLanguage);

      return {
        success: false,
        previousLanguage,
        newLanguage: lang,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    } finally {
      setIsLoading(false);
    }
  }, [language, translationCache, getAllMissingKeys, translations]);

  // Optimized translation testing
  const testAllTranslations = useCallback((keyList?: string[]): TranslationValidationResult => {
    const testKeys = keyList || [
      'common.buttons.save',
      'common.buttons.cancel',
      'common.status.loading',
      'interactions.newInteraction',
      'interactions.interaction_modal.customer_required',
      'interactions.interaction_modal.date_required',
      'interactions.interaction_modal.customer_label',
      'interactions.interaction_modal.search_placeholder',
      'interactions.interaction_modal.cancel_button',
      'interactions.interaction_modal.creating_button',
      'interactions.interaction_modal.scheduling_button',
      'forms.validation.required',
      'forms.validation.pleaseSelectCustomer',
      'forms.validation.pleaseSelectDate',
      'navigation.dashboard',
      'navigation.visits',
      'navigation.customers',
      'navigation.interactions'
    ];

    const missingKeys = getAllMissingKeys(testKeys);
    const invalidKeys: string[] = [];
    const warningKeys: string[] = [];

    // Enhanced validation with warning detection
    testKeys.forEach(key => {
      try {
        const translation = t(key);
        if (translation === key) {
          // Key returned as-is, likely missing
          if (!missingKeys.includes(key)) {
            missingKeys.push(key);
          }
        } else if (!translation || translation.trim() === '') {
          invalidKeys.push(key);
        } else if (translation.includes('[FALLBACK]') || translation.includes('[DEBUG]')) {
          // Contains debug markers, treat as warning
          warningKeys.push(key);
        }
      } catch (error) {
        console.error(`🧪 [LanguageContext] Error testing key '${key}':`, error);
        invalidKeys.push(key);
      }
    });

    const result: TranslationValidationResult = {
      isValid: missingKeys.length === 0 && invalidKeys.length === 0,
      missingKeys,
      invalidKeys,
      totalKeys: testKeys.length,
      coverage: ((testKeys.length - missingKeys.length - invalidKeys.length) / testKeys.length) * 100
    };

    // Add warning information to the result
    (result as any).warningKeys = warningKeys;
    (result as any).warnings = warningKeys.length;

    setLastValidationResult(result);
    return result;
  }, [getAllMissingKeys, t]);

  const exportTranslationReport = useCallback(() => {

    // Run a quick validation test
    const currentValidation = testAllTranslations();

    // Analyze translation structure
    const structureAnalysis = {
      hasInteractions: !!translations.interactions,
      hasInteractionModal: !!(translations.interactions && translations.interactions.interaction_modal),
      modalKeys: translations.interactions?.interaction_modal ? Object.keys(translations.interactions.interaction_modal).length : 0,
      topLevelSections: Object.keys(translations).length,
      sectionSizes: (Object.keys(translations) as Array<keyof TranslationStructure>).reduce<Record<string, number>>((acc, key) => {
        const sectionValue = translations[key];
        if (sectionValue && typeof sectionValue === 'object') {
          acc[String(key)] = Object.keys(sectionValue as Record<string, unknown>).length;
        } else {
          acc[String(key)] = 0;
        }
        return acc;
      }, {})
    };

    const report = {
      timestamp: new Date().toISOString(),
      currentLanguage: language,
      isLoading,
      cachedLanguages: Array.from(translationCache.keys()),
      translationErrors: translationErrors.slice(-15), // Last 15 errors
      lastValidation: lastValidationResult,
      currentValidation,
      structureAnalysis,
      availableKeys: Object.keys(translations).length > 0 ?
        Object.keys(translations).slice(0, 15) : [], // More sample keys
      performance: {
        cacheSize: translationCache.size,
        loadAttempts: Object.fromEntries(loadAttempts),
        errorCount: translationErrors.length,
        averageLoadTime: translationErrors.length > 0 ? 'N/A' : 'Success',
        memoryUsage: JSON.stringify(translations).length
      },
      health: {
        criticalErrors: translationErrors.filter(e => e.error.includes('Missing interactions.interaction_modal')).length,
        structuralIssues: translationErrors.filter(e => e.error.includes('nested') || e.error.includes('structure')).length,
        missingKeys: currentValidation.missingKeys.length,
        invalidKeys: currentValidation.invalidKeys.length,
        overallHealth: currentValidation.isValid && translationErrors.length < 5 ? 'Healthy' :
                       currentValidation.coverage > 80 ? 'Good' :
                       currentValidation.coverage > 60 ? 'Fair' : 'Poor'
      }
    };

    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.table({
        'Language': language,
        'Loading': isLoading,
        'Cache': translationCache.size,
        'Errors': translationErrors.length,
        'Coverage': `${currentValidation.coverage.toFixed(1)}%`
      });
    }

    return report;
  }, [language, isLoading, translationCache, translationErrors, lastValidationResult, translations, loadAttempts, testAllTranslations]);

  const simulateTranslationFailure = useCallback((shouldFail: boolean) => {
    setSimulateFailure(shouldFail);
  }, []);

  // Optimized error recovery methods
  const retryTranslationLoad = useCallback(async (lang: Language): Promise<boolean> => {
    try {
      const attempts = loadAttempts.get(lang) || 0;
      setLoadAttempts(prev => new Map(prev.set(lang, attempts + 1)));
      await loadTranslations(lang);
      return true;
    } catch {
      return false;
    }
  }, [loadAttempts]);

  const clearTranslationCache = useCallback(() => {
    setTranslationCache(new Map());
    setLoadAttempts(new Map());
    setTranslationErrors([]);
    setLastValidationResult(null);
    loadTranslations(language).catch(() => {});
  }, [language]);

  const preloadLanguage = useCallback(async (lang: Language): Promise<boolean> => {
    if (translationCache.has(lang)) {
      return true;
    }

    try {
      const response = await fetch(`/lang_${lang}.json`);
      if (!response.ok) {
        throw new Error(`Failed to preload ${lang}: ${response.status}`);
      }

      const data = await response.json();
      const langData = data[lang];

      if (langData) {
        setTranslationCache(prev => new Map(prev.set(lang, langData)));
        return true;
      } else {
        throw new Error(`No translations found for language: ${lang}`);
      }
    } catch (error) {
      setTranslationErrors(prev => [...prev.slice(-19), {
        key: `preload_${lang}`,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: Date.now()
      }]);
      return false;
    }
  }, [translationCache]);

  // Flush missing key queue (limit to 30 errors)
  useEffect(() => {
    if (missingKeyQueueRef.current.length > 0) {
      const queuedErrors = [...missingKeyQueueRef.current];
      missingKeyQueueRef.current = [];

      setTranslationErrors(prev => {
        const combined = [...prev, ...queuedErrors];
        return combined.slice(-30);
      });
    }
  });

  useEffect(() => {
    if (!isInitialized || isLoading) {
      return;
    }
    fallbackWarningLoggedRef.current = false;
  }, [isInitialized, isLoading]);

  // Optimized context value with reduced dependencies
  const contextValue = useMemo<LanguageContextType>(() => ({
    language,
    setLanguage,
    translations,
    t,
    isLoading,
    isInitialized,
    validateTranslationKey,
    getAllMissingKeys,
    getTranslationStatus,
    switchLanguageWithValidation,
    exportTranslationReport,
    testAllTranslations,
    simulateTranslationFailure,
    retryTranslationLoad,
    clearTranslationCache,
    preloadLanguage
  }), [
    language,
    translations,
    t,
    isLoading,
    isInitialized
  ]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Optimized useLanguage hook
export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  // Development mode utilities
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    (window as any).__languageContext = {
      exportReport: context.exportTranslationReport,
      testTranslations: context.testAllTranslations,
      clearCache: context.clearTranslationCache,
      getStatus: context.getTranslationStatus,
      preloadLanguage: context.preloadLanguage
    };
  }

  // Return safe wrapper during initialization
  if (!context.isInitialized) {
    return {
      ...context,
      t: (key: string, fallback?: string) => fallback || key,
      getTranslationStatus: () => ({ loading: true, error: null, language: context.language }),
      validateTranslationKey: () => false,
      getAllMissingKeys: () => [],
      testAllTranslations: () => ({ isValid: false, missingKeys: [], invalidKeys: [], totalKeys: 0, coverage: 0 }),
      exportTranslationReport: () => ({ error: 'Context not ready' }),
      switchLanguageWithValidation: async () => ({ success: false, previousLanguage: context.language, newLanguage: context.language, error: 'Context not ready' }),
      retryTranslationLoad: async () => false,
      preloadLanguage: async () => false
    };
  }

  return context;
};